/**
 * ErrorBoundary
 *
 * There was no error boundary anywhere in this app, so a single render throw in
 * any panel unmounted the whole tree and left the user staring at the cream
 * background with no explanation and no way forward.
 *
 * The error text and component stack are shown only under `import.meta.env.DEV`.
 * In a production build a stack trace leaks file paths, component names and
 * sometimes interpolated data, so users get a recovery action and nothing else.
 */

import { Component } from 'react'
import { AlertOctagon, RotateCcw } from 'lucide-react'

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { error: null, componentStack: '' }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, info) {
    // Kept in state rather than logged, so the dev-only <details> can show it.
    this.setState({ componentStack: info?.componentStack ?? '' })
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    const { error, componentStack } = this.state

    if (!error) return this.props.children

    return (
      <div className="boundary-shell" role="alert">
        <div className="boundary-card">
          <div className="boundary-icon">
            <AlertOctagon size={22} aria-hidden="true" />
          </div>

          <h1 className="boundary-title">Something went wrong</h1>
          <p className="boundary-text">
            This part of CloudIQ Studio stopped unexpectedly. Reloading usually clears it. If it
            keeps happening, let your platform administrator know what you were doing.
          </p>

          <button type="button" className="btn btn-primary" onClick={this.handleReload}>
            <RotateCcw size={15} aria-hidden="true" />
            Reload
          </button>

          {import.meta.env.DEV && (
            <details className="boundary-details">
              <summary>Developer details</summary>
              <pre className="boundary-pre">{String(error?.stack || error?.message || error)}</pre>
              {componentStack && <pre className="boundary-pre">{componentStack}</pre>}
            </details>
          )}
        </div>
      </div>
    )
  }
}
